import { useContext, useMemo, useState } from "react";
import ToastContext from "../../../context/toastProvider";
import LabelCardEditor from "./LabelCardEditor";
import { emptyManualCard } from "./emptyManualCard";
import { enrichCardLive } from "./enrichCardLive";
import { validateSelectedTags } from "./validateTagsLive";
import { overflowKeysBySerial } from "./limitString";

export default function ManualTagsWizard({
  missing,
  onComplete,
  onCancel,
  fontsReady = true,
}) {
  const { badToast } = useContext(ToastContext);
  const [index, setIndex] = useState(0);
  const [queue, setQueue] = useState(() => [...missing]);
  const [filled, setFilled] = useState([]);
  const [draft, setDraft] = useState(() => emptyManualCard(missing[0]));
  const [errorData, setErrorData] = useState(null);

  const serial = queue[index];
  const enriched = useMemo(() => enrichCardLive(draft), [draft]);
  const overflowBySerial = useMemo(
    () => overflowKeysBySerial(errorData),
    [errorData]
  );
  const overflowKeys = overflowBySerial[draft?.serial] || null;

  const draftForSerial = (serialNumber, cards = filled) => {
    const existing = cards.find((c) => c.serial === serialNumber);
    return existing ? { ...existing } : emptyManualCard(serialNumber);
  };

  const handleVoltar = () => {
    if (index > 0) {
      const newIndex = index - 1;
      const prevSerial = queue[newIndex];
      const existing = filled.find((c) => c.serial === prevSerial);
      const nextFilled = filled.filter((c) => {
        const serialIdx = queue.indexOf(c.serial);
        return serialIdx >= 0 && serialIdx < newIndex;
      });
      setFilled(nextFilled);
      setDraft(existing ? { ...existing } : emptyManualCard(prevSerial));
      setErrorData(null);
      setIndex(newIndex);
    } else {
      onCancel();
    }
  };

  const handlePular = () => {
    const nextQueue = queue.filter((_, i) => i !== index);
    if (nextQueue.length === 0) {
      onComplete(filled);
      return;
    }
    const newIndex = Math.min(index, nextQueue.length - 1);
    const nextSerial = nextQueue[newIndex];
    setQueue(nextQueue);
    setDraft(draftForSerial(nextSerial));
    setErrorData(null);
    setIndex(newIndex);
  };

  const handleProximo = () => {
    const card = { ...draft, select: true };
    const errors = validateSelectedTags([card]);
    if (errors.cards.length) {
      setErrorData(errors);
      badToast?.("Corrija os campos antes de continuar");
      return;
    }
    setErrorData(null);
    const nextFilled = [...filled, card];
    if (index >= queue.length - 1) {
      onComplete(nextFilled);
      return;
    }
    const newIndex = index + 1;
    const nextSerial = queue[newIndex];
    setFilled(nextFilled);
    setDraft(draftForSerial(nextSerial, nextFilled));
    setErrorData(null);
    setIndex(newIndex);
  };

  const updateField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  if (!serial) {
    return null;
  }

  const { card, error } = enriched;

  return (
    <div className="manual-tags-wizard">
      <header className="mb-4">
        <p className="text-sm text-gray-300">
          Serial {index + 1} de {queue.length} não encontrados
        </p>
        <p className="text-lg font-bold text-[#d59106]">{serial}</p>
      </header>

      {error ? (
        <p className="text-sm text-red-400 py-4">
          Subgrades incompletas — preencha as quatro notas ou deixe todas em branco para gerar
          esta etiqueta.
        </p>
      ) : (
        <LabelCardEditor
          key={`wizard-${serial}-${fontsReady ? "fonts" : "nofonts"}`}
          card={card}
          overflowKeys={overflowKeys}
          onField={updateField}
        />
      )}

      <div className="flex gap-2 mt-4">
        <button type="button" onClick={handleVoltar}>
          Voltar
        </button>
        <button type="button" onClick={handlePular}>
          Pular
        </button>
        <button type="button" onClick={handleProximo}>
          Próximo
        </button>
      </div>
    </div>
  );
}
